import { Extension, Node } from '@tiptap/core';
import Image from '@tiptap/extension-image';
import {
  Table,
  TableCell,
  TableHeader,
  TableRow,
} from '@tiptap/extension-table';
import TextAlign from '@tiptap/extension-text-align';
import {
  BackgroundColor,
  Color,
  FontFamily,
  FontSize,
  TextStyle,
} from '@tiptap/extension-text-style';
import StarterKit from '@tiptap/starter-kit';
import { scopeStyle } from 'manage/scope_style';
import { INLINE_TAGS } from 'manage/tiptap/html';

const TEXT_ALIGN_TYPES = ['heading', 'paragraph', 'textDiv'];

// 各ノードとマークが自分の属性として読み書きするものは、残す属性に入れると二重に書き出される
const OWN_ATTRIBUTES = {
  image: ['src', 'alt', 'title', 'width', 'height'],
  link: ['href', 'target', 'rel', 'class'],
  orderedList: ['start', 'type'],
  tableCell: ['colspan', 'rowspan', 'colwidth'],
  tableHeader: ['colspan', 'rowspan', 'colwidth'],
};
const OWN_STYLES = {
  ...Object.fromEntries(TEXT_ALIGN_TYPES.map((type) => [type, ['text-align']])),
  textStyle: ['color', 'background-color', 'font-family', 'font-size'],
};

const scratch = document.createElement('span');

const removeStyles = (style, properties) => {
  scratch.setAttribute('style', style);
  for (const property of properties) {
    scratch.style.removeProperty(property);
  }
  const rest = scratch.getAttribute('style');
  scratch.removeAttribute('style');
  return rest;
};

// 残した属性は編集の画面の DOM にそのまま描くので、スクリプトを動かすものは読まず、失われるものとして HTML の編集で開く
const URL_ATTRIBUTES = ['href', 'src', 'action', 'formaction', 'xlink:href'];
// ブラウザは URL の前後の空白と途中の制御文字を無視して javascript: と読む
const isJavaScriptUrl = (value) =>
  /^javascript:/i.test(
    [...value].filter((char) => char.charCodeAt(0) > 0x20).join(''),
  );

const isSafeAttribute = (name, value) =>
  /^[a-zA-Z_:][-a-zA-Z0-9_:.]*$/.test(name) &&
  !/^on/i.test(name) &&
  name.toLowerCase() !== 'srcdoc' &&
  !(URL_ATTRIBUTES.includes(name.toLowerCase()) && isJavaScriptUrl(value));

// ソースの編集やほかのツールからの貼り付けで入った class や style をスキーマが落とさないように、ノードとマークが読まない属性をまるごと持たせる
const PreservedAttributes = Extension.create({
  name: 'preservedAttributes',

  addGlobalAttributes() {
    return [
      'blockquote',
      'bulletList',
      'div',
      'heading',
      'horizontalRule',
      'iframe',
      'image',
      'link',
      'listItem',
      'orderedList',
      'paragraph',
      'table',
      'tableCell',
      'tableHeader',
      'tableRow',
      'textDiv',
      'textStyle',
    ].map((type) => ({
      types: [type],
      attributes: {
        htmlAttributes: {
          default: null,
          parseHTML: (element) => {
            const attributes = {};
            for (const { name, value } of element.attributes) {
              if (OWN_ATTRIBUTES[type]?.includes(name)) continue;
              if (!isSafeAttribute(name, value)) continue;
              const rest =
                name === 'style' && OWN_STYLES[type]
                  ? removeStyles(value, OWN_STYLES[type])
                  : value;
              if (rest) attributes[name] = rest;
            }
            return Object.keys(attributes).length > 0 ? attributes : null;
          },
          renderHTML: ({ htmlAttributes }) => htmlAttributes ?? {},
        },
      },
    }));
  },
});

const INLINE_CHILD_TAGS = new Set([...INLINE_TAGS, 'BR', 'IFRAME', 'IMG']);

const hasBlockChild = (element) =>
  [...element.children].some((child) => !INLINE_CHILD_TAGS.has(child.tagName));

// CKEditor のソースの編集で作られた <div> のレイアウトを、中身を編集できる形で残す
const Div = Node.create({
  name: 'div',
  group: 'block',
  content: 'block+',
  defining: true,

  parseHTML() {
    return [
      { tag: 'div', getAttrs: (element) => hasBlockChild(element) && null },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', HTMLAttributes, 0];
  },
});

// 文字だけを入れた <div> を Div で読むと中身が <p> に包まれて余白が変わるので、段落と同じように文字を直接持たせる
const TextDiv = Node.create({
  name: 'textDiv',
  group: 'block',
  content: 'inline*',

  parseHTML() {
    return [
      { tag: 'div', getAttrs: (element) => !hasBlockChild(element) && null },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', HTMLAttributes, 0];
  },
});

const createPlaceholder = (label, detail) => {
  const dom = document.createElement('span');
  dom.className = 'c-tiptap__placeholder';
  dom.contentEditable = 'false';
  dom.textContent = label;
  dom.title = detail;
  return dom;
};

// 埋め込みを編集の中で読み込むと、地図や動画が操作を奪うので箱で見せる
const Iframe = Node.create({
  name: 'iframe',
  group: 'inline',
  inline: true,
  atom: true,

  parseHTML() {
    return [{ tag: 'iframe' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['iframe', HTMLAttributes];
  },

  addNodeView() {
    return ({ node }) => ({
      dom: createPlaceholder(
        '埋め込み（iframe）',
        node.attrs.htmlAttributes?.src ?? '',
      ),
    });
  },
});

// <style> の CSS をそのまま描くと管理画面の全体に効いて崩れるので、編集の画面では編集の領域に閉じ込めて効かせ、保存する HTML は書かれたままにする
// CSS だけを描くと見えなくなり、範囲を選んで消したときに気づかないまま CSS ごと消えるので、目印は残す
const StyleBlock = Node.create({
  name: 'styleBlock',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      html: {
        default: '',
        parseHTML: (element) => element.outerHTML,
        rendered: false,
      },
    };
  },

  parseHTML() {
    return [{ tag: 'style' }];
  },

  renderHTML({ node }) {
    const template = document.createElement('template');
    template.innerHTML = node.attrs.html;
    return template.content.firstChild;
  },

  addNodeView() {
    return ({ node }) => {
      const dom = createPlaceholder('CSS（<style>）', node.attrs.html);
      const template = document.createElement('template');
      template.innerHTML = node.attrs.html;
      const style = document.createElement('style');
      style.textContent = scopeStyle(
        template.content.textContent,
        '.c-tiptap__content',
      );
      dom.append(style);
      return { dom };
    };
  },
});

// 何にも囲まれていないスタンプス記法（html.js の markBareStampsSyntax が目印にしたもの）は、<p> で包むと保存したときに見た目が変わるので、目印のまま書き出して tidyTiptapHtml で記法の文字列に戻す
const StampsSyntax = Node.create({
  name: 'stampsSyntax',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      syntax: {
        default: '',
        parseHTML: (element) => element.dataset.syntax,
        rendered: false,
      },
    };
  },

  parseHTML() {
    return [{ tag: 'stamps-syntax' }];
  },

  renderHTML({ node }) {
    return ['stamps-syntax', { 'data-syntax': node.attrs.syntax }];
  },

  // 表示のときに FormatHelper#convert_stamps_syntax が置き換えるボタンと同じクラスで描く
  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement('div');
      dom.className = 'c-tiptap__stampsSyntax';
      dom.contentEditable = 'false';
      dom.title = node.attrs.syntax;
      const label = createPlaceholder(
        'QRコードの取得ボタン',
        node.attrs.syntax,
      );
      const button = document.createElement('span');
      button.className = 'btn-flat btn-block btn-cl-orange';
      button.textContent = node.attrs.syntax.replace(
        /^\[\[GET_STAMP:[0-9]+,(.+)\]\]$/,
        '$1',
      );
      dom.append(label, button);
      return { dom };
    };
  },
});

// TextStyle は style の無い <span> を読まないが、class や id だけを持つ <span> もある
const PreservedTextStyle = TextStyle.extend({
  parseHTML() {
    return this.parent().map((rule) => ({
      ...rule,
      getAttrs: (element) =>
        element.hasAttribute('style')
          ? rule.getAttrs(element)
          : element.attributes.length > 0 && {},
    }));
  },
});

// Table は列の幅の <colgroup> と min-width を書き足すが、CKEditor の表には無かった
const PlainTable = Table.extend({
  renderHTML({ HTMLAttributes }) {
    return ['table', HTMLAttributes, ['tbody', 0]];
  },
});

// 既定の 1 の colspan と rowspan や、align から読み替えた style を書き足すと、CKEditor の表と形が変わる
const cellAttributes = () => ({
  colspan: {
    default: 1,
    renderHTML: ({ colspan }) => (colspan === 1 ? {} : { colspan }),
  },
  rowspan: {
    default: 1,
    renderHTML: ({ rowspan }) => (rowspan === 1 ? {} : { rowspan }),
  },
  colwidth: { default: null, rendered: false },
});
const PlainTableCell = TableCell.extend({ addAttributes: cellAttributes });
const PlainTableHeader = TableHeader.extend({ addAttributes: cellAttributes });

export const buildExtensions = () => [
  StarterKit.configure({
    // 整形済みは CKEditor と違って <pre><code> で書き出すので読み込まず、失われるものとして HTML の編集で開く
    codeBlock: false,
    // 開いただけで末尾に段落が足され、何も変えずに保存しても HTML が変わる
    trailingNode: false,
    link: {
      openOnClick: false,
      autolink: false,
      // CKEditor のリンクは target と rel を付けていなかった
      HTMLAttributes: { target: null, rel: null },
    },
  }),
  // CKEditor は画像を段落の中に置く
  Image.configure({ inline: true }),
  TextAlign.configure({
    types: TEXT_ALIGN_TYPES,
    alignments: ['left', 'center', 'right', 'justify'],
  }),
  PreservedTextStyle,
  Color,
  BackgroundColor,
  FontFamily,
  FontSize,
  PlainTable.configure({ resizable: false }),
  TableRow,
  PlainTableCell,
  PlainTableHeader,
  Div,
  TextDiv,
  Iframe,
  StyleBlock,
  StampsSyntax,
  PreservedAttributes,
];
